import { useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitInquiry } from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function collectTracking() {
    return {
        user_agent: navigator.userAgent || null,
        referrer: document.referrer || null,
        page_url: window.location.href || null,
        screen: window.screen ? `${window.screen.width}x${window.screen.height}` : null,
        language: navigator.language || null,
        timezone: Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || null,
        platform: navigator.userAgentData?.platform || navigator.platform || null,
        client_timestamp: new Date().toISOString(),
    };
}

function fireChecklistTrack(checklist_type) {
    fetch(`${API_URL}/api/track/checklist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checklist_type, ...collectTracking() }),
    }).catch(() => {});
}

function fireChecklistLeadTrack(name, email, checklist_type) {
    fetch(`${API_URL}/api/track/checklist-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, checklist_type, ...collectTracking() }),
    }).catch(() => {});
}

const MAIN_LOGO =
    "/assets/tc-auditors-logo-black.png";

const SECTIONS = [
    {
        title: "1 · Company & engagement information",
        items: [
            "Registered company name and trading name",
            "CIPC registration number",
            "VAT number (if registered)",
            "Income Tax reference number",
            "PAYE / UIF / SDL reference numbers",
            "Year-end date confirmed",
            "Public Interest Score (PIS) calculated for the current year",
            "MOI on file (latest version) and BEE Affidavit (if applicable)",
            "Beneficial Ownership register up to date with CIPC",
            "Confirmation of any changes in directors / shareholders during the year",
        ],
    },
    {
        title: "2 · Banking & cash",
        items: [
            "All bank statements covering the full year (including closed accounts)",
            "Bank confirmation letters at year-end (signed)",
            "Petty cash count sheets and reconciliations",
            "Credit card statements and supporting receipts",
            "Foreign currency bank accounts converted at year-end spot rate",
            "Outstanding cheques / EFTs reconciled",
        ],
    },
    {
        title: "3 · Debtors (accounts receivable)",
        items: [
            "Aged debtors listing at year-end (matching trial balance)",
            "Identification of doubtful debts and bad-debt write-offs",
            "Subsequent receipts schedule (post year-end)",
            "Customer statements / confirmations where required",
            "Provision for ECL (IFRS 9) calculation supported",
        ],
    },
    {
        title: "4 · Creditors (accounts payable)",
        items: [
            "Aged creditors listing at year-end (matching trial balance)",
            "Outstanding supplier invoices / accruals not yet captured",
            "Subsequent payments schedule (post year-end)",
            "Loan accounts and shareholder loan reconciliations",
            "PAYE, VAT, SDL, UIF and Income Tax payable accruals",
        ],
    },
    {
        title: "5 · Inventory",
        items: [
            "Stock count sheets signed at year-end",
            "Costing methodology (FIFO / weighted average) consistent with prior year",
            "Slow-moving and obsolete stock write-downs identified",
            "Goods-in-transit and consignment stock reconciled",
        ],
    },
    {
        title: "6 · Fixed assets",
        items: [
            "Fixed asset register reconciled to general ledger",
            "Additions schedule with supporting invoices",
            "Disposals schedule with proceeds and recoupments calculated",
            "Wear-and-tear / depreciation policy applied consistently",
            "Section 12B / 12E / 12C accelerated allowances considered",
            "Impairment indicators reviewed (IAS 36 / IFRS for SMEs s.27)",
        ],
    },
    {
        title: "7 · Payroll & employees",
        items: [
            "EMP201 monthly submissions for the full year",
            "Annual EMP501 reconciliation submitted",
            "IRP5 / IT3(a) certificates issued and reconciled",
            "Leave provision calculated (where material)",
            "Bonus accrual calculated to year-end",
            "COIDA / Workmen's Compensation ROE submitted and assessment paid",
            "UIF declarations (UI-19) lodged for any terminations",
        ],
    },
    {
        title: "8 · Tax compliance",
        items: [
            "Provisional tax IRP6/01 and IRP6/02 submitted; IRP6/03 (top-up) considered",
            "VAT201 monthly / bi-monthly returns reconciled to ledger",
            "Output VAT vs input VAT balance reconciled to VAT control account",
            "Dividends declared and Dividends Tax (DTR01) settled within 30 days",
            "Capital gains and losses calculated and supported",
            "Section 11(e) / 12E / 12C wear-and-tear claims supported",
            "Foreign tax credits (s.6quat) supported with foreign tax certificates",
        ],
    },
    {
        title: "9 · Compliance, contracts & legal",
        items: [
            "All material contracts signed during the year (lease, finance, supply)",
            "Insurance policies renewed and premiums up to date",
            "Legal correspondence / letters of demand / litigation matters",
            "SARS correspondence (verification, audit, objection letters)",
            "POPIA compliance review / privacy policy update",
            "Subsequent events from year-end to sign-off date",
        ],
    },
    {
        title: "10 · Tax-saving opportunities to ask TC Auditors about",
        items: [
            "Small Business Corporation (s.12E) eligibility re-tested",
            "Solar / renewable energy assets — section 12B 100% write-off",
            "Home office expense claims for owners working from home",
            "Travel allowance — fixed-cost vs actual cost analysis",
            "Section 18A donations to PBOs (10% of taxable income deduction)",
            "Retirement annuity contributions (27.5% of greater of remuneration / taxable income)",
            "Medical aid tax credits and additional medical expenses",
            "Restraint of trade / restraint payment structuring",
            "Profit-extraction analysis: salary vs dividend optimisation",
        ],
    },
];

export default function YearEndTaxChecklist() {
    const handlePrint = () => {
        fireChecklistTrack("yearend");
        window.print();
    };

    return (
        <div className="bg-tca-fog min-h-screen" data-testid="page-checklist-year-end-tax">
            {/* Toolbar (hidden on print) */}
            <div className="print-hide bg-white border-b border-tca-line sticky top-[110px] z-20">
                <div className="container-tca py-4 flex items-center justify-between gap-3">
                    <Link
                        to="/checklists"
                        data-testid="back-to-checklists"
                        className="btn-ghost text-base gap-2.5"
                    >
                        <ArrowLeft className="w-5 h-5" /> All checklists
                    </Link>
                    <button
                        onClick={handlePrint}
                        data-testid="print-checklist-btn"
                        className="btn-primary"
                    >
                        <Printer className="w-4 h-4" /> Print or save as PDF
                    </button>
                </div>
            </div>

            {/* Email capture (hidden on print) */}
            <EmailCaptureCard />

            {/* Printable container */}
            <article className="container-tca my-10">
                <div className="print-page bg-white border border-tca-line max-w-[920px] mx-auto p-10 sm:p-14 shadow-sm">
                    {/* Header */}
                    <header className="flex items-start justify-between gap-6 pb-8 border-b-4 border-tca-orange">
                        <div className="flex-1">
                            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-tca-orange mb-2">
                                TC Auditors Annual Pack · Established 2015
                            </p>
                            <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-tca-ink leading-[1.02] tracking-tight">
                                Year-End Tax Checklist
                            </h1>
                            <p className="mt-3 text-tca-mute text-sm max-w-xl leading-relaxed">
                                The full prep list TC Auditors uses to close a client's books and prepare a SARS-ready set of Annual Financial Statements. Tick the items as you compile your file.
                            </p>
                        </div>
                        <img
                            src={MAIN_LOGO}
                            alt="TC Auditors"
                            className="h-14 w-auto select-none shrink-0"
                        />
                    </header>

                    {/* Client meta */}
                    <section className="grid grid-cols-2 gap-x-8 gap-y-4 my-8 print-section">
                        {[
                            ["Client / Company name", ""],
                            ["Year-end date", ""],
                            ["Engagement partner", ""],
                            ["Date prepared", ""],
                        ].map(([label]) => (
                            <div key={label}>
                                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-tca-mute mb-2">
                                    {label}
                                </p>
                                <div className="border-b-2 border-tca-line h-6" />
                            </div>
                        ))}
                    </section>

                    {/* Sections */}
                    {SECTIONS.map((s) => (
                        <section key={s.title} className="my-8 print-section">
                            <h2 className="font-display font-extrabold text-tca-ink text-xl mb-4 border-l-4 border-tca-orange pl-3">
                                {s.title}
                            </h2>
                            <ul className="space-y-2.5">
                                {s.items.map((it, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm text-tca-ink leading-snug">
                                        <input
                                            type="checkbox"
                                            className="mt-1 w-4 h-4 accent-tca-orange shrink-0"
                                            aria-label={it}
                                        />
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}

                    {/* Notes */}
                    <section className="my-8 print-section">
                        <h2 className="font-display font-extrabold text-tca-ink text-xl mb-4 border-l-4 border-tca-orange pl-3">
                            Notes for the engagement team
                        </h2>
                        <div className="space-y-3">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="border-b border-tca-line h-7" />
                            ))}
                        </div>
                    </section>

                    {/* Footer block */}
                    <footer className="mt-10 pt-6 border-t border-tca-line grid grid-cols-1 sm:grid-cols-2 gap-6 text-xs text-tca-mute">
                        <div>
                            <p className="font-display font-bold text-tca-ink mb-1">TC Auditors (Pty) Ltd</p>
                            <p>2015/290284/07 · Xero Gold Partner</p>
                            <p>42 Boet Kruger Street, Meyerton, 1961</p>
                        </div>
                        <div className="sm:text-right">
                            <p>admin@tcauditors.co.za</p>
                            <p>Reception: 079 461 7545</p>
                            <p>Help Desk: 060 015 1110</p>
                        </div>
                        <p className="sm:col-span-2 italic leading-relaxed">
                            This checklist is provided as a general guide. Every business has unique facts that change the answer — please book a consultation with TC Auditors before relying on any item on this list as advice.
                        </p>
                    </footer>
                </div>
            </article>
        </div>
    );
}


// =====================================================================
// Email-capture lead-magnet card. Posts to /api/inquiries with
// source="checklist-yearend" — backend then dispatches the actual
// checklist email to the lead via Microsoft Graph.
// =====================================================================
function EmailCaptureCard() {
    const [form, setForm] = useState({ name: "", email: "", company: "" });
    const [status, setStatus] = useState("idle"); // idle | loading | success
    const [errors, setErrors] = useState({});

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
    const handleEmailChange = (e) => {
        const val = e.target.value;
        setForm((f) => ({ ...f, email: val }));
        if (val.length === 0) {
            setErrors((er) => ({ ...er, email: undefined }));
        } else if (!EMAIL_RE.test(val.trim())) {
            setErrors((er) => ({ ...er, email: "Please enter a valid email address (e.g. name@example.co.za)." }));
        } else {
            setErrors((er) => ({ ...er, email: undefined }));
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const er = {};
        if (form.name.trim().length < 2) er.name = "Please enter your full name.";
        if (!EMAIL_RE.test(form.email.trim())) er.email = "Please enter a valid email address (e.g. name@example.co.za).";
        if (Object.keys(er).length) {
            setErrors(er);
            return;
        }
        setStatus("loading");
        try {
            await submitInquiry({
                name: form.name,
                email: form.email,
                company: form.company || null,
                subject: "Year-End Tax Checklist requested",
                message: `Year-End Tax Checklist requested via website. Please send a copy to ${form.email}.`,
                source: "checklist-yearend",
            });
            // Fire-and-forget admin tracking notification
            fireChecklistLeadTrack(form.name, form.email, "yearend");
            setStatus("success");
            toast.success("Sent! Check your inbox shortly.");
        } catch (err) {
            console.error(err);
            setStatus("idle");
            toast.error("Could not send the checklist. Please try again.");
        }
    };

    return (
        <section className="print-hide bg-white border-b border-tca-line">
            <div className="container-tca py-8 md:py-10">
                <div className="border border-tca-orange bg-tca-orangeSoft/40 p-6 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" data-testid="yearend-email-capture">
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Mail className="w-4 h-4 text-tca-orange" />
                            <p className="overline text-tca-orange">Free · Emailed instantly</p>
                        </div>
                        <h3 className="font-display font-extrabold text-tca-ink text-2xl leading-tight">
                            Want a copy emailed to you?
                        </h3>
                        <p className="mt-2 text-sm text-tca-mute leading-relaxed">
                            We will send the full checklist plus a printable link to your inbox — perfect for forwarding to your team or filing for next year.
                        </p>
                    </div>

                    <div className="lg:col-span-8">
                        {status === "success" ? (
                            <div
                                data-testid="yearend-capture-success"
                                className="flex items-start gap-3 bg-white border border-tca-line p-5"
                            >
                                <CheckCircle2 className="w-6 h-6 text-tca-orange shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="font-display font-bold text-tca-ink mb-1">
                                        On its way, {form.name.split(" ")[0]}.
                                    </p>
                                    <p className="text-sm text-tca-mute">
                                        Sent to <strong className="text-tca-ink">{form.email}</strong>. If you don't see it within a few minutes, check spam or try a different address.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStatus("idle");
                                        setForm({ name: "", email: "", company: "" });
                                    }}
                                    data-testid="yearend-capture-reset"
                                    className="btn-ghost shrink-0"
                                >
                                    Send another
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                className="grid grid-cols-1 sm:grid-cols-12 gap-3"
                                data-testid="yearend-capture-form"
                                noValidate
                            >
                                {/* Row 1: Name + Email */}
                                <div className="sm:col-span-6">
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Your name"
                                        data-testid="yearend-capture-name"
                                        aria-label="Your name"
                                        className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                                    />
                                    {errors.name && (
                                        <p className="text-[11px] text-red-600 mt-1">{errors.name}</p>
                                    )}
                                </div>
                                <div className="sm:col-span-6">
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={handleEmailChange}
                                        placeholder="you@company.co.za"
                                        data-testid="yearend-capture-email"
                                        aria-label="Your email"
                                        className={`w-full border px-4 py-3 text-sm focus:outline-none focus:ring-2 bg-white transition-colors ${
                                            errors.email
                                                ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                                                : "border-tca-line focus:border-tca-orange focus:ring-tca-orangeSoft"
                                        }`}
                                    />
                                    {errors.email && (
                                        <p className="text-[11px] text-red-600 mt-1">{errors.email}</p>
                                    )}
                                </div>
                                {/* Row 2: Company (wide) + Submit */}
                                <div className="sm:col-span-9">
                                    <input
                                        type="text"
                                        value={form.company}
                                        onChange={update("company")}
                                        placeholder="Company name (optional)"
                                        data-testid="yearend-capture-company"
                                        aria-label="Company (optional)"
                                        className="w-full border border-tca-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-tca-orange focus:ring-2 focus:ring-tca-orangeSoft"
                                    />
                                </div>
                                <div className="sm:col-span-3">
                                    <button
                                        type="submit"
                                        disabled={status === "loading"}
                                        data-testid="yearend-capture-submit"
                                        className="btn-primary w-full disabled:opacity-60"
                                    >
                                        {status === "loading" ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>Send</>
                                        )}
                                    </button>
                                </div>
                                <p className="sm:col-span-12 text-[11px] text-tca-mute mt-1">
                                    By submitting, you agree to receive the checklist from TC Auditors. We will never share your details — read our privacy approach in the footer.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
